import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Building2,
  RefreshCw,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import { getAiDashboardPulse } from '../../services/api/implementation/impl';
import {
  AiCardShell,
  AiInsightError,
  AiInsightSkeleton,
  AiSourceBadge,
  AiCreditsBadge,
  AiCreditsNotice,
  formatAiApiError,
  isAllProjectsAiScope,
  resolveAiProjectId
} from './AiUiShared';
import AiCreditsRechargeAction from './AiCreditsRechargeAction';
import AiPulseDetailDrawer from './AiPulseDetailDrawer';
import {
  isBuilderAccountOwner,
  resolveAllowedVerticals,
  resolveDefaultVertical
} from './aiVerticalScope';

const CACHE_STALE_MS = 15 * 60 * 1000;

const BULLET_ICONS = {
  overall: Sparkles,
  collection: BarChart3,
  forecast: TrendingUp,
  inventory: Building2,
  overdue: AlertTriangle
};

const BULLET_STYLES = {
  overall: 'text-gray-600',
  collection: 'text-blue-600',
  forecast: 'text-indigo-600',
  inventory: 'text-emerald-600',
  overdue: 'text-red-600'
};

function normalizeBullet(bullet) {
  if (typeof bullet === 'string') {
    return { category: 'overall', text: bullet };
  }
  return {
    category: bullet?.category || 'overall',
    text: bullet?.text || ''
  };
}

export default function AiPulsePanel({
  projectId,
  defaultVertical = null,
  /** When set (or when defaultVertical is set), hide cross-vertical tabs — dashboard shows one lens only. */
  fixedVertical = null,
  /** Builder portfolio / main dashboard — show all vertical tabs (builder only). */
  showAllVerticals = false
}) {
  const navigate = useNavigate();
  const apiProjectId = resolveAiProjectId(projectId);
  const allProjects = isAllProjectsAiScope(projectId);
  const isBuilder = isBuilderAccountOwner();
  const lockedVertical =
    showAllVerticals && isBuilder ? null : fixedVertical ?? defaultVertical ?? null;

  const verticalOptions = useMemo(
    () => resolveAllowedVerticals(isBuilder, { fixedVertical: lockedVertical }),
    [isBuilder, lockedVertical]
  );

  const [vertical, setVertical] = useState(() =>
    resolveDefaultVertical(
      showAllVerticals && isBuilder ? defaultVertical ?? 'sales' : lockedVertical,
      isBuilder
    )
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  React.useEffect(() => {
    setVertical(
      resolveDefaultVertical(
        showAllVerticals && isBuilder ? defaultVertical ?? 'sales' : lockedVertical,
        isBuilder
      )
    );
  }, [lockedVertical, defaultVertical, showAllVerticals, isBuilder, apiProjectId]);

  React.useEffect(() => {
    if (!verticalOptions.some((o) => o.id === vertical)) {
      setVertical(verticalOptions[0]?.id ?? 'sales');
    }
  }, [vertical, verticalOptions]);

  const activeVerticalLabel =
    verticalOptions.find((o) => o.id === vertical)?.label ?? 'Sales';

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
    queryKey: ['aiDashboardPulse', apiProjectId, vertical],
    queryFn: () => getAiDashboardPulse(apiProjectId, vertical),
    staleTime: CACHE_STALE_MS,
    gcTime: CACHE_STALE_MS * 2,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });

  const updatedLabel = useMemo(() => {
    if (!data?.generatedAt) return 'Just now';
    const mins = Math.max(
      1,
      Math.round((Date.now() - new Date(data.generatedAt).getTime()) / 60000)
    );
    return mins <= 2 ? 'Updated 2 min ago' : `Updated ${mins} min ago`;
  }, [data?.generatedAt, isFetching]);

  const bullets = useMemo(
    () => (data?.bullets || []).map(normalizeBullet).filter((b) => b.text),
    [data?.bullets]
  );

  const portfolioHealth = data?.metrics?.portfolioHealth;

  const scopeHint = allProjects
    ? 'Portfolio-wide · all projects'
    : data?.scopeLabel || null;

  return (
    <AiCardShell className="!from-white !via-white !to-teal-50/20 !border-teal-100">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles className="h-5 w-5 text-[#1E4A53]" />
          <h3 className="text-sm font-bold uppercase tracking-wider text-gray-900">AI Pulse</h3>
          {!isLoading && !isError ? (
            <AiSourceBadge
              source={data?.source}
              cacheStatus={data?.cacheStatus}
              llmInvoked={data?.llmInvoked}
            />
          ) : null}
          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-[10px] font-semibold text-emerald-800">
            {updatedLabel}
          </span>
          {allProjects ? (
            <span className="rounded-full bg-teal-50 px-2.5 py-0.5 text-[10px] font-semibold text-[#1E4A53] ring-1 ring-teal-100">
              All projects
            </span>
          ) : null}
          <AiCreditsBadge aiCredits={data?.aiCredits} />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="rounded-lg p-1.5 text-gray-500 hover:bg-white hover:text-[#1E4A53]"
            title="Refresh pulse">
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            disabled={isLoading || isError}
            className="inline-flex items-center gap-1.5 rounded-xl bg-[#1E4A53] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#163a42] disabled:cursor-not-allowed disabled:opacity-50">
            View detailed insights
            <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {verticalOptions.length > 1 ? (
        <div className="mb-3 flex flex-wrap gap-1 rounded-xl bg-white p-1 ring-1 ring-gray-200">
          {verticalOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              onClick={() => setVertical(option.id)}
              className={`rounded-lg px-3 py-1 text-xs font-semibold ${
                vertical === option.id
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}>
              {option.label}
            </button>
          ))}
        </div>
      ) : (
        <p className="mb-3 text-xs font-semibold text-[#1E4A53]">
          {activeVerticalLabel} insights
        </p>
      )}

      {scopeHint ? (
        <p className="mb-3 text-xs font-medium text-[#1E4A53]/90">{scopeHint}</p>
      ) : null}

      <AiCreditsNotice aiCredits={data?.aiCredits} />
      <AiCreditsRechargeAction aiCredits={data?.aiCredits} />

      {isLoading ? (
        <AiInsightSkeleton lines={5} />
      ) : isError ? (
        <AiInsightError
          message={formatAiApiError(error)}
          onRetry={() => refetch()}
        />
      ) : (
        <>
          <p className="mb-4 text-sm leading-relaxed text-gray-800">
            {data?.summaryBox || data?.headline || 'No pulse summary yet.'}
          </p>

          <ul className="mb-4 space-y-2.5">
            {bullets.map((bullet, idx) => {
              const Icon = BULLET_ICONS[bullet.category] || Sparkles;
              const iconColor = BULLET_STYLES[bullet.category] || 'text-gray-500';
              return (
                <li key={idx} className="flex gap-2.5 text-sm text-gray-700">
                  <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${iconColor}`} />
                  <span>{bullet.text}</span>
                </li>
              );
            })}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-gray-500">{data?.footer?.label}</span>
            {portfolioHealth != null && vertical !== 'sales' ? (
              <span className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#1E4A53] ring-1 ring-teal-200">
                Portfolio Health: {portfolioHealth}/100
              </span>
            ) : null}
          </div>
        </>
      )}

      <AiPulseDetailDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        pulse={data}
        projectId={apiProjectId}
        onNavigate={(path) => {
          setDrawerOpen(false);
          navigate(path);
        }}
        onRefresh={() => refetch()}
        isRefreshing={isFetching}
      />
    </AiCardShell>
  );
}
